import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { InventorizePhotoLlmClient } from '@/features/inventorization/inventorize-photo-llm-client';
import { type AiExtraction, CAPTURE_SESSION_STATUSES, type CaptureSession } from '@/shared/capture/capture-session';
import { AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';
import { INVENTORIZE_EXTRACTION_BACKEND_STATUSES } from '@/shared/ocr/inventorize-extraction-contract';
import {
  getLatestInventorizeExtractionJob,
  isServerInventorizeExtractionJobOutput,
  mapBackendStatusToLocalStatus,
} from '@/shared/ocr/inventorize-extraction-jobs';
import { messages } from './messages';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 80;

interface ExtractionPollingState {
  error: string | null;
  isPolling: boolean;
}

export function useInventorizeExtractionPolling(input: {
  onRefresh: () => Promise<unknown> | unknown;
  session: CaptureSession | null | undefined;
}) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const services = useInventoryAppServices();
  const repositories = useMemo(
    () => ({
      captureSessions: services.captureSessions,
    }),
    [services.captureSessions],
  );
  const llmClient = useMemo(() => new InventorizePhotoLlmClient(), []);
  const latestJob = input.session ? getLatestInventorizeExtractionJob(input.session.aiExtractions) : undefined;
  const latestJobRef = useRef(latestJob);
  const onRefreshRef = useRef(input.onRefresh);
  const localJobId = latestJob?.id;
  const backendJobId =
    latestJob && isServerInventorizeExtractionJobOutput(latestJob.output) ? latestJob.output.jobId : undefined;
  const [state, setState] = useState<ExtractionPollingState>({
    error: null,
    isPolling: false,
  });

  useEffect(() => {
    latestJobRef.current = latestJob;
  }, [latestJob]);

  useEffect(() => {
    onRefreshRef.current = input.onRefresh;
  }, [input.onRefresh]);

  useEffect(() => {
    const currentJob = latestJobRef.current;

    if (
      !(
        currentJob &&
        backendJobId &&
        currentJob.id === localJobId &&
        isServerInventorizeExtractionJobOutput(currentJob.output)
      )
    ) {
      setState({ error: null, isPolling: false });

      return undefined;
    }

    if (currentJob.status !== AI_EXTRACTION_STATUSES.queued && currentJob.status !== AI_EXTRACTION_STATUSES.running) {
      setState({ error: currentJob.errorCode ?? null, isPolling: false });

      return undefined;
    }

    if (!activeLocation?.apiKey) {
      setState({
        error: intl.formatMessage(messages.llmPollLocationApiKeyRequired),
        isPolling: false,
      });

      return undefined;
    }

    const target = { backendJobId, localJobId: currentJob.id };
    const locationApiKey = activeLocation.apiKey;
    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      setState({ error: null, isPolling: true });

      try {
        const job = latestJobRef.current;

        if (
          !(
            job &&
            job.id === target.localJobId &&
            isServerInventorizeExtractionJobOutput(job.output) &&
            (job.status === AI_EXTRACTION_STATUSES.queued || job.status === AI_EXTRACTION_STATUSES.running)
          )
        ) {
          setState({ error: job?.errorCode ?? null, isPolling: false });

          return;
        }

        const output = job.output;
        const response = await llmClient.getJob({
          jobId: target.backendJobId,
          locationApiKey,
        });
        const isFinished =
          response.status === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.completed ||
          response.status === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.failed;
        const hasFailed = response.status === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.failed;
        const updatedAt = new Date().toISOString();
        const nextJob: AiExtraction = {
          ...job,
          completedAt: isFinished ? (job.completedAt ?? updatedAt) : undefined,
          errorCode: hasFailed ? (response.error?.code ?? 'LLM_EXTRACTION_FAILED') : undefined,
          output: {
            ...output,
            backendStatus: response.status,
            error: response.error,
            result: response.result,
          },
          status: mapBackendStatusToLocalStatus(response.status),
        };

        await repositories.captureSessions.update(job.sessionId, session => ({
          ...session,
          aiExtractions: session.aiExtractions.map(extraction => (extraction.id === job.id ? nextJob : extraction)),
          status:
            session.status === CAPTURE_SESSION_STATUSES.draft ? CAPTURE_SESSION_STATUSES.needsReview : session.status,
          updatedAt,
        }));

        if (!isCancelled) {
          await onRefreshRef.current();
        }

        if (isCancelled || isFinished) {
          setState({
            error: hasFailed
              ? (response.error?.message ?? intl.formatMessage(messages.photoWizardExtractionFailed))
              : null,
            isPolling: false,
          });

          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          setState({
            error: intl.formatMessage(messages.llmPollTimeout),
            isPolling: false,
          });

          return;
        }

        timeoutId = setTimeout(() => {
          void poll();
        }, POLL_INTERVAL_MS);
      } catch (error) {
        setState({
          error: error instanceof Error ? error.message : intl.formatMessage(messages.llmPollError),
          isPolling: false,
        });
      }
    }

    void poll();

    return () => {
      isCancelled = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeLocation?.apiKey, backendJobId, intl, llmClient, localJobId, repositories.captureSessions]);

  return state;
}
